import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import sadMan from '../assets/bg.png';
import video from '../assets/bg.mp4';
import audioFile from '../assets/audio/fridge.mp3';

// Основной контейнер с фоновым изображением грустного человека у холодильника
const SplashContainer = styled(motion.div)`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  overflow: hidden;
  background-color: #000;
`;

// Фоновое изображение с отдельной анимацией
const BackgroundImg = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${sadMan});
  background-size: cover;
  background-position: center;
  z-index: -2;
`;

// Видео контейнер
const VideoBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: -2;
`;

// Стиль для самого видео
const BackgroundVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

// Тёмный оверлей который появляется после видео
const Overlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  z-index: -1;
`;

// Виньетка для драматического эффекта
const Vignette = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  box-shadow: inset 0 0 200px rgba(0, 0, 0, 0.9);
  z-index: -1;
  opacity: 0;
`;

// Контейнер для текста с атмосферным размытием
const TextContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 85%;
  max-width: 500px;
  backdrop-filter: blur(3px);
  background: rgba(0, 0, 0, 0.4);
  padding: 2.5rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    padding: 2rem;
    width: 90%;
  }
`;

// Стилизованный заголовок без UPPERCASE 
const Title = styled(motion.h1)`
  font-size: 3.8rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 1rem;
  position: relative;
  text-transform: none;
  font-family: 'Black Han Sans', sans-serif;
  font-weight: 700;
  letter-spacing: -0.5px;
  line-height: 1.1;
  
  /* Базовый белый цвет */
  color: white;
  
  /* Убрано сияние */
  text-shadow: none;
  
  .white-text {
    color: white;
  }
  
  .gradient-text {
    /* Яркий градиент в фиолетово-розовых тонах */
    background: linear-gradient(
      120deg,
      #ffffff 0%,
      #ff6ad5 25%,
      #c659ff 50%,
      #ff36be 75%,
      #ffffff 100%
    );
    background-size: 400% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    
    /* Плавная анимация переливания градиента */
    animation: gradientFlow 6s ease infinite;
  }
  
  @keyframes gradientFlow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

// Подзаголовок с более серым цветом
const Subtitle = styled(motion.p)`
  font-size: 1.4rem;
  color: #CCCCCC;
  opacity: 1;
  text-align: center;
  margin-bottom: 2rem;
  font-weight: 600;
  line-height: 1.4;
  max-width: 100%;
  letter-spacing: 0.3px;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 1.5rem;
  }
`;

// Неоновая кнопка с более жирным текстом
const ContinueButton = styled(motion.button)`
  padding: 0.9rem 2rem;
  background: linear-gradient(to right, #6C2FF2, #8A2BE2);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  width: auto;
  
  /* Неоновое свечение */
  box-shadow: 
    0 0 5px #6C2FF2,
    0 0 15px #8A2BE2,
    0 0 25px rgba(138, 43, 226, 0.6),
    inset 0 0 5px rgba(255, 255, 255, 0.3);
    
  /* Изменено: пульсация только теней без изменения размера кнопки */
  animation: shadowPulse 2s infinite alternate;
  
  @keyframes shadowPulse {
    0% {
      box-shadow: 
        0 0 5px #6C2FF2,
        0 0 15px #8A2BE2,
        0 0 25px rgba(138, 43, 226, 0.6),
        inset 0 0 5px rgba(255, 255, 255, 0.3);
    }
    100% {
      box-shadow: 
        0 0 15px #6C2FF2,
        0 0 30px #8A2BE2,
        0 0 50px rgba(138, 43, 226, 0.8),
        inset 0 0 10px rgba(255, 255, 255, 0.5);
    }
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 0 15px #6C2FF2,
      0 0 25px #8A2BE2,
      0 0 45px rgba(138, 43, 226, 0.8),
      inset 0 0 15px rgba(255, 255, 255, 0.5);
    animation: none;
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

// Иконка стрелки внутри кнопки
const ArrowIcon = styled(motion.span)`
  margin-left: 8px;
  display: inline-block;
  font-size: 1.1rem;
  
  ${ContinueButton}:hover & {
    animation: pulse 1.5s infinite;
  }
  
  @keyframes pulse {
    0% {
      transform: translateX(0);
    }
    50% {
      transform: translateX(5px);
    }
    100% {
      transform: translateX(0);
    }
  }
`;

// Эффект холодного света из холодильника
const ColdLight = styled(motion.div)`
  position: absolute;
  width: 60%;
  height: 100%;
  background: radial-gradient(
    ellipse at center,
    rgba(116, 222, 255, 0.15) 0%,
    rgba(30, 87, 153, 0.01) 70%,
    rgba(0, 0, 0, 0) 100%
  );
  filter: blur(40px);
  top: 0;
  right: 0;
  z-index: -1;
  opacity: 0;
`;

// Компонент SplashScreen
const SplashScreen = () => {
  const navigate = useNavigate();
  const [isExiting, setIsExiting] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);
  const [uiReady, setUiReady] = useState(false);
  const videoRef = useRef(null);

  // Обработчик окончания видео
  const handleVideoEnd = () => {
    setVideoEnded(true);
  };

  // Обработчик начала воспроизведения видео
  const handleVideoStart = () => {
    setVideoStarted(true);
  };

  // Модифицируем useEffect для предотвращения блика
  useEffect(() => {
    if (videoRef.current) {
      // Установка громкости на ноль для бесшумного воспроизведения
      videoRef.current.muted = true; // Начинаем с отключенным звуком для гарантированного запуска
      
      // Устанавливаем скорость воспроизведения на 1.2
      videoRef.current.playbackRate = 1.4;
      
      // Добавляем слушатель на событие начала воспроизведения
      videoRef.current.addEventListener('playing', handleVideoStart);
      
      // Попытка воспроизведения видео
      const playPromise = videoRef.current.play();
      
      // Обработка ошибок воспроизведения (если автовоспроизведение заблокировано браузером)
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.error("Autoplay was prevented:", error);
          // Если автовоспроизведение заблокировано, просто показываем UI
          setVideoStarted(true);
          setVideoEnded(true);
        });
      }
      
      // Установим таймаут на случай, если видео не загрузится или зависнет
      const timeout = setTimeout(() => {
        if (!videoEnded) {
          setVideoEnded(true);
        }
      }, 4000);
      
      // Добавим общий обработчик для включения звука при любом взаимодействии
      const enableSound = () => {
        if (videoRef.current) {
          videoRef.current.muted = false;
          videoRef.current.volume = 0.5;
          console.log("Sound enabled by user interaction");
        }
      };
      
      document.addEventListener('click', enableSound);
      document.addEventListener('touchstart', enableSound);
      
      // Уменьшаем задержку перед отображением UI с 300мс до 100мс
      const uiTimeout = setTimeout(() => {
        setUiReady(true);
      }, 100);
      
      return () => {
        clearTimeout(timeout);
        if (videoRef.current) {
          videoRef.current.removeEventListener('playing', handleVideoStart);
        }
        document.removeEventListener('click', enableSound);
        document.removeEventListener('touchstart', enableSound);
        clearTimeout(uiTimeout);
      };
    }
  }, []);

  // Эффект для анимированного перехода
  const handleContinue = () => {
    setIsExiting(true);
    setTimeout(() => {
      navigate('/food');
    }, 500);
  };

  // Добавим отладочную информацию в консоль
  useEffect(() => {
    console.log("Component rendered, video element:", videoRef.current);
  }, []);

  return (
    <AnimatePresence>
      {!isExiting ? (
        <SplashContainer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.5 } }}
        >
          {/* Фоновое видео */}
          <VideoBackground
            initial={{ opacity: 0 }}
            animate={{ opacity: videoStarted ? 1 : 0 }}
            transition={{ duration: 1.5 }}
            as={motion.div}
          >
            <BackgroundVideo
              ref={videoRef}
              onEnded={handleVideoEnd}
              muted
              playsInline
              controls={false}
              disablePictureInPicture
              disableRemotePlayback
            >
              <source src={video} type="video/mp4" />
              <source src={video} type="video/webm" />
              Your browser does not support the video tag.
            </BackgroundVideo>
          </VideoBackground>
          
          {/* Тёмный оверлей появляется после окончания видео */}
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: videoEnded ? 1 : 0 }}
            transition={{ duration: 1 }} // Увеличена длительность затемнения
          />
          
          {/* Виньетка для драматического эффекта */}
          <Vignette
            initial={{ opacity: 0 }}
            animate={{ opacity: videoEnded ? 1 : 0 }}
            transition={{ duration: 0.5, delay: 0 }} // Ускорено появление
          />

          {/* Эффект холодного света от холодильника */}
          <ColdLight
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: videoEnded ? [0, 0.7, 0.5] : 0
            }}
            transition={{
              opacity: { 
                duration: 2, 
                delay: 0,
                times: [0, 0.7, 1],
                repeat: Infinity,
                repeatType: "reverse",
                repeatDelay: 1
              }
            }}
          />

          {/* Модифицируем контейнер с текстом и кнопкой */}
          <TextContainer
            initial={{ opacity: 0 }}
            animate={{ opacity: videoEnded && uiReady ? 1 : 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <Title
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: videoEnded ? 0 : -20, opacity: videoEnded ? 1 : 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <span className="gradient-text">Nothing</span>{" "}
              <span className="white-text">to eat</span>{" "}
              <span className="gradient-text">again</span>?
            </Title>
            
            <Subtitle
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: videoEnded ? 0 : 20, opacity: videoEnded ? 1 : 0 }}
              transition={{ duration: 0.4, delay: 0.6 }}
            >
              We've all been there. Same fridge, same problem.
            </Subtitle>
            
            <ContinueButton
              initial={{ y: 20, opacity: 0 }}
              animate={{ 
                y: videoEnded && uiReady ? 0 : 20, 
                opacity: videoEnded && uiReady ? 1 : 0 
              }}
              transition={{ duration: 0.4, delay: 0.9 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleContinue}
            >
              Fix This Now
              <ArrowIcon>→</ArrowIcon>
            </ContinueButton>
          </TextContainer>
        </SplashContainer>
      ) : null}
    </AnimatePresence>
  );
};

export default SplashScreen; 